import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { Product, ProductInterval } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductFAQButton from './ProductFAQButton';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';

interface SponsorshipSubscriptionProps {
  habitatId: string;
  entryPoint?: string; // for analytics only
}

const getProductTitle = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'HELPER';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'FRIEND';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'GUARDIAN';
    default:
      return null;
  }
};

const getProductColor = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'aria-selected:bg-[#FBFFCA] aria-selected:border-[#CDD565]';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'aria-selected:bg-[#FFF2F8] aria-selected:border-[#FFDCEE]';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'aria-selected:bg-[#FFE1CC] aria-selected:border-[#FFB682]';
    default:
      return null;
  }
};

const getTierImageInfo = (lookupKey: string | undefined) => {
  const tier = parseInt(lookupKey?.split('-').at(-1) ?? '1', 10);
  switch (tier) {
    case 2:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t2-no-bg.webp',
        width: 62,
        height: 68,
        tier: 2,
      };
    case 3:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t3-no-bg.webp',
        width: 70,
        height: 73,
        tier: 3,
      };
    default:
      return {
        src: 'https://zoolife.tv/assets/products/l2-t1-no-bg.webp',
        width: 55,
        height: 59,
        tier: 1,
      };
  }
};

const renderTierImage = (lookupKey: string | undefined) => {
  const { src, width, height, tier } = getTierImageInfo(lookupKey);
  return (
    <img src={src} alt={`Sponsorship Tier ${tier}`} width={width} height={height} className="preflight preflight-img" />
  );
};

const SponsorshipSubscription = ({ habitatId, entryPoint }: SponsorshipSubscriptionProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === habitatId));
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const [showLearnMore, setShowLearnMore] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const learnMoreRef = useRef<HTMLDivElement>(null);

  const { data: productsByInterval } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey:
        'monthly-supporter-tier-1,monthly-supporter-tier-2,monthly-supporter-tier-3,annual-supporter-tier-1,annual-supporter-tier-2,annual-supporter-tier-3',
    }),
    select: (data) => {
      const map = data.list.reduce((acc, product) => {
        if (!product.productLookupKey) return acc;
        acc.set(product.productLookupKey, product);
        return acc;
      }, new Map<string, Product>());
      return {
        month: [
          map.get('monthly-supporter-tier-1'),
          map.get('monthly-supporter-tier-2'),
          map.get('monthly-supporter-tier-3'),
        ].filter((product) => product != null),
        year: [
          map.get('annual-supporter-tier-1'),
          map.get('annual-supporter-tier-2'),
          map.get('annual-supporter-tier-3'),
        ].filter((product) => product != null),
      } as Partial<Record<ProductInterval, Product[]>>;
    },
  });

  const products = productsByInterval?.[productInterval];

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  useEffect(() => {
    const container = containerRef.current;
    const children = showLearnMore ? learnMoreRef.current : selectorRef.current;
    if (!container || !children) return undefined;
    container.style.height = `${children.getBoundingClientRect().height}px`;
    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.borderBoxSize[0]?.blockSize || children.getBoundingClientRect().height;
      if (height == null) return;
      container.style.height = `${height}px`;
    });
    resizeObserver.observe(children);
    return () => {
      resizeObserver.unobserve(children);
      resizeObserver.disconnect();
    };
  }, [showLearnMore]);

  return (
    <div className="w-full max-w-[336px] rounded-2xl shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div ref={containerRef} className="relative w-full overflow-hidden rounded-2xl transition-[height]">
        {/* selector */}
        <div ref={selectorRef} className="flex w-full flex-col items-center gap-4 pb-6 pt-3">
          <div className="w-full px-4">
            <ProductFAQButton className="text-black" />
            <h4 className="preflight preflight-h4 mb-3 mt-1 text-center text-2xl/none font-bold text-green-900">
              Make an Impact
            </h4>
            <p className="preflight preflight-p text-center text-sm text-stone-500">
              100% of proceeds fund the animal care behind this camera; you’ll receive updates as our community unlocks new
              enrichments.
            </p>
          </div>
          <ProductIntervalSelector
            value={productInterval}
            onChange={(interval) => {
              if (interval === productInterval) return;
              setProduct(null);
              setProductInterval(interval);
            }}
            className="-mt-1"
          />
          <div className="grid w-full gap-2 px-2" style={{ gridTemplateColumns: `repeat(${products?.length ?? 0}, 1fr)` }}>
            {products?.map((p) => (
              <button
                role="option"
                type="button"
                aria-selected={p._id === product?._id}
                className={clsx(
                  'preflight preflight-button flex flex-col items-center justify-between rounded-2xl border-2 border-[#CFDEAC] pb-2 pt-3 text-base text-secondary-green',
                  getProductColor(p.productLookupKey),
                )}
                key={p._id}
                onClick={() => setProduct(p)}
              >
                <h6 className="preflight preflight-h6 text-center text-[15px]/6 font-bold text-secondary-green">
                  {getProductTitle(p.productLookupKey)}
                </h6>
                <span className="text-base/none">
                  <ProductPrice
                    currency={p.price.currency}
                    price={p.price.price}
                    className="font-normal leading-none [font-size:inherit]"
                  />
                  /{productInterval === 'month' ? 'Mo' : 'Yr'}
                </span>
                <div className="flex grow items-center justify-center">{renderTierImage(p.productLookupKey)}</div>
              </button>
            ))}
          </div>
          <div className="w-full px-6">
            <Button
              disabled={isPending || !habitat || !product?.eligibility?.allowed}
              size="manual"
              className="w-full px-4 py-3 text-base/none font-bold"
              onClick={() => {
                if (!product || !habitat) return;
                checkout({
                  productId: product._id,
                  priceId: product.price.priceId,
                  associatedHabitatIds: [habitatId],
                  successPath: `/h/${habitat.zoo.slug}/${habitat.slug}?tab=sponsors&sponsored=1`,
                  cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}?ppt=sponsor`,
                });
                trackMP('request-checkout', {
                  productId: product._id,
                  priceId: product.price.priceId,
                  productLookupKey: product.productLookupKey,
                  habitatId,
                  entryPoint,
                });
              }}
            >
              {!product ? 'Select an Option' : 'Support Now'}
            </Button>
            {product && !product.eligibility?.tierChangeableUserProductId && (
              <ProductEligibilityResult result={product.eligibility} className="mt-3" />
            )}
          </div>
          <button
            type="button"
            className="preflight preflight-button flex items-center justify-center gap-1 text-sm text-stone-500"
            onClick={() => setShowLearnMore(!showLearnMore)}
          >
            <span>How Do These Proceeds Make an Impact?</span>
            <ArrowRight className="size-4" />
          </button>
        </div>

        {/* learn more */}
        <div
          aria-hidden={!showLearnMore}
          className="absolute inset-0 bg-white transition-transform aria-hidden:pointer-events-none aria-hidden:translate-x-full"
        >
          <div ref={learnMoreRef} className="flex flex-col items-center pb-7 pt-3">
            <button
              type="button"
              className="preflight preflight-button mb-3 flex items-center justify-center gap-1 text-sm text-stone-500"
              onClick={() => setShowLearnMore(!showLearnMore)}
            >
              <ArrowRight className="size-4 rotate-180" />
              <span>Back</span>
            </button>
            <div className="flex flex-col items-center justify-center px-4 text-[13px]/5 text-stone-500">
              <h4 className="preflight preflight-h4 text-center text-xl/6 font-bold text-green-900">Where We Can Help</h4>
              <p className="preflight preflight-p my-3 text-pretty text-center">
                Animal care is expensive. Zoos and sanctuaries typically don’t have large budgets for enrichment. That’s
                where you come in.
              </p>
              <img
                src="https://zoolife.tv/assets/products/sponsorship-how-it-works-3.webp"
                width={318}
                height={176}
                alt="Sponsorship How It Works"
                className="preflight preflight-img"
              />
              <p className="preflight preflight-p mt-3 text-pretty text-center">
                Animal enrichments provide fun challenges for the animals, to stimulate natural behaviours, get rewarded, and
                stay active.
              </p>
              <p className="preflight preflight-p mt-3 text-pretty text-center">
                Whenever the group milestone is reached, a new enrichment will be placed into the habitat.
              </p>
              <Button
                size="manual"
                className="mt-6 w-full px-4 py-3 text-base/none font-bold"
                onClick={() => {
                  setShowLearnMore(false);
                }}
              >
                See Support Options
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorshipSubscription;
